<template>
  <div class="loading-screen">
    <div
      v-bind="$attrs"
      class="background fixed top-0 z-50 flex h-full w-full items-center justify-center select-none"
      @click="handleLoadingScreenClick"
    >
      <div
        class="sm:surface sm:shadow-secondary/50 relative flex flex-col justify-center space-y-8 sm:size-120 sm:rounded-3xl sm:shadow-[0_0_32px_10px]"
        :class="{ 'animate-glow': shouldGlow }"
      >
        <!-- Tenor GIF overlay - only show after 5 clicks -->
        <div
          v-show="shouldGlow"
          class="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center overflow-hidden bg-black sm:rounded-3xl"
        >
          <div
            class="tenor-gif-embed pointer-events-none relative flex h-full w-full items-center justify-center object-contain before:pointer-events-none before:absolute before:top-0 before:right-0 before:left-0 before:z-1 before:h-10 before:bg-black before:content-[''] after:pointer-events-none after:absolute after:top-0 after:right-0 after:bottom-0 after:left-0 after:z-10 after:bg-transparent after:content-['']"
            :data-postid="selectedPostId"
            data-share-method="host"
            data-aspect-ratio="1.235"
            data-width="100%"
          >
            <a :href="`https://tenor.com/view/${selectedPostId}`">Monkey GIF</a>
          </div>
        </div>

        <div class="flex flex-col items-center">
          <img
            src="@/assets/img/logo-peanuts-vertical.svg"
            alt="Logo Peanuts"
            width="384"
            height="384"
            fetchpriority="high"
          />
        </div>

        <div
          class="text-gray-lighten-1 dark:text-gray-lighten-1-dark relative mb-6 w-full p-2 text-center text-xl font-normal"
        >
          <span>
            {{ displayLoadingText }}
          </span>
          <span class="loading-dots inline-block w-8 text-left" aria-hidden="true"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from "vue"
import { storeToRefs } from "pinia"
import monkeyScream from "@/assets/monkey-scream.mp3"
import { RoleTypes } from "@/enums/role-types"
import { trackPageLoadComplete } from "@/services/application-insights-service"
import { useAuthStore } from "@/store/auth.store"

defineOptions({
  inheritAttrs: false
})

const props = defineProps<{
  /** Fixed status text; when set, joke rotation is paused. */
  message?: string
}>()

const { activeUser } = storeToRefs(useAuthStore())
const loadingStartedAt = performance.now()
const loadingText = ref("")
const displayLoadingText = computed(() => props.message || loadingText.value)

const loadingTexts = [
  "Met gemeente overleggen",
  "Breedvloerplaten hijsen",
  "Kraanmachinist bellen",
  "Vrachtwagen volgooien",
  "Koffie halen",
  "Zand storten",
  "Wagen voorladen",
  "Asfalt walsen",
  "Cement mixen",
  "Stenen stapelen",
  "Bouwtekening controleren",
  "Veiligheidshelmen uitdelen",
  "Graafmachine bedienen",
  "Staalbalken positioneren",
  "Betonspecie aanmaken",
  "Werkoverleg houden",
  "Pauze inlassen",
  "Gereedschap onderhouden",
  "Metingen uitvoeren",
  "Materiaal bestellen",
  "Sloopwerk voorbereiden",
  "Lunchpauze nemen",
  "Tegels zagen",
  "Waterpas stellen",
  "Elektriciteit aanleggen",
  "Isolatiemateriaal snijden",
  "Veiligheidscontrole uitvoeren",
  "Afval sorteren",
  "Kabelsleuven graven",
  "Asfalt aanleggen",
  "Riolering vernieuwen",
  "Straatstenen leggen",
  "Verlichting plaatsen",
  "Bermen versterken",
  "Brug inspecteren",
  "Wegen markeren",
  "Goten reinigen",
  "Verkeersborden vervangen",
  "Leidingen verleggen",
  "Waterkering bouwen",
  "Voetpaden herstellen",
  "Bewegwijzering updaten",
  "Groenonderhoud plegen",
  "Tunnels controleren",
  "Fietspaden aanleggen",
  "Parkeerplaatsen markeren",
  "Boomwortels beschermen",
  "Grondwater managen",
  "Signalering testen",
  "Drainage verbeteren",
  "Spoorlijnen controleren",
  "Onderdoorgang schoonmaken",
  "Bouwkeet plaatsen"
]

const tenorPostIds = ["7259279", "26888602", "16416201"]

let nextTimeoutMs = 0
let textTimeout: ReturnType<typeof setTimeout> | null = null
let audio: HTMLAudioElement | null = null
let tenorScript: HTMLScriptElement | null = null

const clickCount = ref(0)
const selectedPostId = ref(getRandomPostId())

const shouldGlow = computed(() => clickCount.value >= 5)

function getRandomPostId() {
  const idx = Math.floor(Math.random() * tenorPostIds.length)
  return tenorPostIds[idx]
}

function setRandomLoadingText() {
  nextTimeoutMs = Math.floor(Math.random() * 4000) + 1000
  loadingText.value = loadingTexts[Math.floor(Math.random() * loadingTexts.length)]
}

function cycleLoadingText() {
  // Always drop any pending tick first so this stays safe to call at any time
  // (mount, or from the props.message watcher) without ever leaving two
  // overlapping timeout chains running.
  if (textTimeout) {
    clearTimeout(textTimeout)
    textTimeout = null
  }
  if (props.message) return
  setRandomLoadingText()
  textTimeout = setTimeout(() => {
    cycleLoadingText()
  }, nextTimeoutMs)
}

// props.message is set for the initial "Gebruiker zoeken" auth fetch and clears
// once that resolves, while the same LoadingScreen instance stays mounted for
// the rest of the loading window. Without this watcher the onMounted call above
// early-returns on the fixed message, no tick is ever scheduled, and loadingText
// stays "" forever - showing an empty line with only the animated dots.
watch(() => props.message, cycleLoadingText)

function handleLoadingScreenClick() {
  clickCount.value++
  if (clickCount.value === 5) {
    if (!audio) {
      audio = new Audio(monkeyScream)
    }
    audio.currentTime = 0
    audio.play()
    // Pick a random GIF postid
    selectedPostId.value = getRandomPostId()
    // Don't reset clickCount so GIF stays visible
  }
}

onMounted(() => {
  // Start animations
  cycleLoadingText()

  // Load Tenor embed script dynamically
  tenorScript = document.createElement("script")
  tenorScript.src = "https://tenor.com/embed.js"
  tenorScript.async = true
  tenorScript.type = "text/javascript"
  document.head.appendChild(tenorScript)
})

onUnmounted(() => {
  if (!activeUser.value?.knownBusinessEntityContact?.hasRole(RoleTypes.SERVICE_PROVIDER)) {
    trackPageLoadComplete(
      performance.now() - loadingStartedAt,
      activeUser.value?.activeTenant?.name?.trim() || "unknown"
    )
  }

  // Clean up intervals and timeouts
  if (textTimeout) {
    clearTimeout(textTimeout)
    textTimeout = null
  }

  // Clean up audio
  if (audio) {
    audio.pause()
    audio = null
  }

  // Clean up dynamically added script
  if (tenorScript && document.head.contains(tenorScript)) {
    document.head.removeChild(tenorScript)
    tenorScript = null
  }
})
</script>

<style scoped>
@keyframes loadingDots {
  0% {
    content: "";
  }

  25% {
    content: ".";
  }

  50% {
    content: "..";
  }

  75% {
    content: "...";
  }

  100% {
    content: "";
  }
}

.loading-dots::after {
  content: "";
  animation: loadingDots 2s infinite;
}

@keyframes glow {
  0% {
    box-shadow: 0 0 32px 10px rgba(232, 110, 0, 0.32);
  }

  50% {
    box-shadow: 0 0 56px 18px rgba(232, 110, 0, 0.48);
  }

  100% {
    box-shadow: 0 0 32px 10px rgba(232, 110, 0, 0.32);
  }
}

.animate-glow {
  animation: glow 3s ease-in-out infinite;
}
</style>
